import { createPath, Location } from "react-router";
import { AppRoute } from "@/app/router/utils";
import { NavigationPath } from "@/store/navigation/types";
import { SETTINGS_TEMPLATE_ID_SEARCH_PARAM } from "@/app/router/appRoutes";

const SETTINGS_SEARCH_PARAM = "settings";
const TEMPLATES_SETTINGS_VALUE = "templates";
const TAB_STATE_SEARCH_PARAMS = ["tid", SETTINGS_SEARCH_PARAM, SETTINGS_TEMPLATE_ID_SEARCH_PARAM];

export const convertPathToString = (path: NavigationPath): string => {
  // Path is string
  if (typeof path === "string") return path;

  // Path is appRoute
  if ("path" in path) return (path as AppRoute).path;

  // Path is To object
  const toObj = path as { pathname?: string; search?: string; hash?: string };
  return createPath({ pathname: toObj.pathname || "/", search: toObj.search || "", hash: "" });
};

export const stripTidFromPath = (path: string): string => {
  // eslint-disable-next-line no-restricted-properties
  const url = new URL(path, window.location.origin);
  url.searchParams.delete("tid");
  return url.pathname + url.search;
};

export const stripTabStateFromPath = (path: string): string => {
  // eslint-disable-next-line no-restricted-properties
  const url = new URL(path, window.location.origin);

  if (url.searchParams.get(SETTINGS_SEARCH_PARAM) === TEMPLATES_SETTINGS_VALUE) {
    const templateId = url.searchParams.get(SETTINGS_TEMPLATE_ID_SEARCH_PARAM);
    if (templateId) {
      for (const param of TAB_STATE_SEARCH_PARAMS) {
        url.searchParams.delete(param);
      }
      const search = url.searchParams.size > 0 ? `?${url.searchParams.toString()}` : "";
      return `/templates/${encodeURIComponent(templateId)}${search}`;
    }
  }

  for (const param of TAB_STATE_SEARCH_PARAMS) {
    url.searchParams.delete(param);
  }

  return url.pathname + url.search;
};

export const convertPathToStringWithoutTid = (path: NavigationPath): string => {
  const pathString = convertPathToString(path);
  return stripTidFromPath(pathString);
};

export const convertPathToTabPathString = (path: NavigationPath): string => {
  const pathString = convertPathToString(path);
  return stripTabStateFromPath(pathString);
};

/**
 * Removes specified query parameters from the current URL and returns a new Location object.
 * Handles the common pattern of deleting query params and formatting the result.
 *
 * @param location - The current location object
 * @param paramsToRemove - Array of query parameter names to remove
 * @returns A new Location object with the specified params removed
 */
interface RemoveQueryParamsParams {
  location: Location;
  paramsToRemove: string[];
}
export const removeQueryParams = ({
  location,
  paramsToRemove,
}: RemoveQueryParamsParams): Location => {
  const params = new URLSearchParams(location.search);

  for (const param of paramsToRemove) {
    params.delete(param);
  }

  const search = params.size > 0 ? `?${params.toString()}` : "";

  return {
    ...location,
    search,
  };
};
